import {
  ApartmentOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Popover } from "antd";
import { FC } from "react";
import { PROJECT_PHASE_LABELS } from "../../projectPhase";
import { collectProjectOverdueContentItems } from "../ProjectContentItemList";
import TaskPriorityTag from "../TaskPriorityTag";
import "./index.css";

type ProjectOverdueContentItem = ReturnType<
  typeof collectProjectOverdueContentItems
>[number];

interface ProjectOverdueBadgeProps {
  getMemberDisplayName: (username: string) => string;
  items: ProjectOverdueContentItem[];
  projectName: string;
}

function formatDate(timestamp: number) {
  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(timestamp));
}

function getOverdueDays(dueTime: number) {
  return Math.max(
    1,
    Math.ceil((Date.now() - dueTime) / (24 * 60 * 60 * 1000))
  );
}

function getContentText(content: string) {
  const wrapper = document.createElement("div");
  wrapper.innerHTML = content || "";
  return (
    (wrapper.textContent || "").replace(/\s+/g, " ").trim() ||
    (wrapper.querySelector("img") ? "图片内容" : "未填写内容")
  );
}

const ProjectOverdueBadge: FC<ProjectOverdueBadgeProps> = ({
  getMemberDisplayName,
  items,
  projectName,
}) => {
  if (!items.length) {
    return null;
  }

  const content = (
    <section
      aria-label={`${projectName}逾期详情`}
      className="projectOverduePopover"
    >
      <header className="projectOverduePopover__header">
        <div className="projectOverduePopover__heading">
          <span><WarningOutlined /></span>
          <div>
            <strong>逾期内容明细</strong>
            <small title={projectName}>{projectName}</small>
          </div>
        </div>
        <div className="projectOverduePopover__summary">
          <strong>{items.length}</strong>
          <span>项逾期</span>
        </div>
      </header>
      <div className="projectOverduePopover__list">
        {items.map((item) => {
          const assignees = item.assignees
            .map((username) => getMemberDisplayName(username) || username)
            .filter(Boolean);
          const contentText = getContentText(item.content);
          return (
            <article
              key={`${item.parentTaskId || item.phase}-${
                item.subtaskId || item.subtaskTitle
              }-${item.item_id}`}
            >
              <div className="projectOverduePopover__itemTop">
                <div>
                  <span className="projectOverduePopover__phase">
                    {PROJECT_PHASE_LABELS[item.phase]}
                  </span>
                  <TaskPriorityTag priority={item.priority} />
                </div>
                <em>逾期 {getOverdueDays(item.due_time)} 天</em>
              </div>
              <strong
                className="projectOverduePopover__content"
                title={contentText}
              >
                {contentText}
              </strong>
              <div className="projectOverduePopover__subtask">
                <ApartmentOutlined />
                <span>所属子任务</span>
                <strong title={item.subtaskTitle}>{item.subtaskTitle}</strong>
              </div>
              <footer className="projectOverduePopover__meta">
                <div>
                  <span><TeamOutlined /></span>
                  <div>
                    <small>负责人</small>
                    <strong title={assignees.join("、")}>
                      {assignees.join("、") || "未分配负责人"}
                    </strong>
                  </div>
                </div>
                <div className="is-overdue">
                  <span><ClockCircleOutlined /></span>
                  <div>
                    <small>预计完结</small>
                    <strong>{formatDate(item.due_time)}</strong>
                  </div>
                </div>
              </footer>
            </article>
          );
        })}
      </div>
    </section>
  );

  return (
    <Popover
      content={content}
      mouseEnterDelay={0.12}
      overlayClassName="projectOverduePopoverOverlay"
      placement="bottomRight"
      trigger="hover"
    >
      <span
        aria-label={`查看${projectName}的 ${items.length} 项逾期详情`}
        className="projectOverviewMiniCard__overdueBadge"
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <WarningOutlined />
        <strong>{items.length}</strong> 项逾期
      </span>
    </Popover>
  );
};

export default ProjectOverdueBadge;
